import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type HomepageStats = {
  creators: number;
  clicks: number;
  rating: number;
  sales: number;
};

const FALLBACK: HomepageStats = { creators: 600, clicks: 700, rating: 4.9, sales: 0 };

export const useHomepageStats = () => {
  return useQuery({
    queryKey: ["homepage-stats"],
    queryFn: async (): Promise<HomepageStats> => {
      const { data, error } = await supabase.rpc("get_public_homepage_stats");
      if (error || !data) return FALLBACK;
      const d = data as Record<string, unknown>;
      return {
        creators: Number(d.creators ?? FALLBACK.creators),
        clicks: Number(d.clicks ?? FALLBACK.clicks),
        rating: Number(d.rating ?? FALLBACK.rating),
        sales: Number(d.sales ?? FALLBACK.sales),
      };
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: FALLBACK,
  });
};

// Round down to a nice "social proof" number: 602 -> "600+", 6380 -> "6.3K+"
export const formatStatNumber = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 100) return `${n}`;
  if (n < 1000) return `${Math.floor(n / 50) * 50}+`;
  if (n < 10000) return `${(Math.floor(n / 100) / 10).toFixed(1)}K+`;
  if (n < 1000000) return `${Math.floor(n / 1000)}K+`;
  return `${(Math.floor(n / 100000) / 10).toFixed(1)}M+`;
};